import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  HelpCircle,
  Search,
  BookOpen,
  CreditCard,
  Shield,
  Users,
  FileText,
  MessageCircle,
  Mail,
  ExternalLink,
  Heart,
  TrendingUp,
  CheckCircle,
  Send,
  Loader2,
} from 'lucide-react';
import apiClient from '../services/api';

const HelpCenterPage = () => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Contact form state
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactError, setContactError] = useState('');

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactError('');
    
    try {
      await apiClient.post('/enquiries/help', {
        name: contactForm.name,
        email: contactForm.email,
        category: 'help-center',
        subject: contactForm.subject,
        message: contactForm.message,
      });
      setContactSubmitted(true);
    } catch (err) {
      console.error('Failed to submit help request:', err);
      setContactError('Failed to send message. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  const categories = [
    {
      icon: Heart,
      title: 'Getting Started',
      description: 'Learn how to create and manage campaigns',
      articles: 5,
      color: 'text-rose-500',
      content: [
        { title: 'How to create your first campaign', answer: 'Click "Start a Campaign" from the navigation menu. Fill in your campaign title, description, and set your funding goal. Add compelling images and videos, then submit for review. Once approved, your campaign goes live!' },
        { title: 'Setting up your profile', answer: 'Go to Settings > Profile to add your photo, bio, and social links. A complete profile builds trust with potential donors and increases your campaign visibility.' },
        { title: 'Understanding campaign verification', answer: 'Complete email verification, phone verification, and KYC (Know Your Customer) to earn trust badges. Verified campaigns appear higher in search results and receive more donations.' },
        { title: 'Choosing the right category', answer: 'Select a category that best matches your cause. Popular categories include Medical, Education, Emergency, and Community. The right category helps donors find your campaign.' },
        { title: 'Writing a compelling story', answer: 'Share your authentic story with specific details. Explain why you need help, how funds will be used, and the impact donations will make. Include photos and regular updates.' },
      ],
    },
    {
      icon: CreditCard,
      title: 'Payments & Donations',
      description: 'Payment methods, fees, and withdrawals',
      articles: 8,
      color: 'text-emerald-500',
      content: [
        { title: 'Accepted payment methods', answer: 'We accept Visa, Mastercard, American Express, Discover, debit cards, Apple Pay, and Google Pay. All transactions are secured with 256-bit SSL encryption.' },
        { title: 'Platform fees explained', answer: 'FundTheHope charges 0% platform fees. Standard payment processing fees charged by payment providers (e.g., 2.9% + $0.30) apply.' },
        { title: 'How to withdraw funds', answer: 'Go to Wallet > Withdraw, add your bank account details, and request a withdrawal. Funds typically arrive in 3-5 business days. Minimum withdrawal is $10.' },
        { title: 'Setting up automatic withdrawals', answer: 'Enable automatic withdrawals in Wallet settings to receive funds weekly or monthly without manual requests.' },
        { title: 'International donations', answer: 'We accept donations from countries worldwide. Currency conversion is handled automatically at competitive exchange rates.' },
        { title: 'Recurring donations', answer: 'Donors can set up monthly recurring donations to support campaigns over time. Campaign creators can see recurring donor statistics in their dashboard.' },
        { title: 'Refund policy', answer: 'Donations are generally non-refundable once processed. Refunds may be considered on a case-by-case basis solely at FundTheHope\'s discretion in cases of confirmed fraud or material policy violations. We do not guarantee refunds.' },
        { title: 'Tax-deductible donations', answer: 'Tax deductibility depends on the campaign type and your location. We provide donation receipts for your records. Consult a tax professional for specific advice.' },
      ],
    },
    {
      icon: Shield,
      title: 'Trust & Safety',
      description: 'Verification, security, and fraud prevention',
      articles: 6,
      color: 'text-blue-500',
      content: [
        { title: 'How we work to protect users', answer: 'Campaigns undergo review before approval. We use fraud detection tools and require identity verification for withdrawals. Note: FundTheHope is an intermediary platform and cannot guarantee campaign legitimacy or outcomes. Donors contribute at their own risk.' },
        { title: 'Campaign verification process', answer: 'Campaigns are reviewed within 24-48 hours. We check for compliance with our guidelines. Verification indicates guideline compliance but does not guarantee campaign accuracy or outcomes.' },
        { title: 'Identity verification (KYC)', answer: 'Upload a government-issued ID and take a selfie for verification. This helps build trust but does not guarantee how funds will be used.' },
        { title: 'Reporting suspicious activity', answer: 'Click the "Report" button on any campaign page or contact trust@fundthehope.com. Our team reviews all reports and takes appropriate action based on our policies.' },
        { title: 'Secure payment processing', answer: 'All payments are processed through Stripe with PCI-DSS Level 1 compliance. We never store your full card details.' },
        { title: 'Privacy and data protection', answer: 'We follow GDPR guidelines and never sell your personal data. You control what information is visible on your profile.' },
      ],
    },
    {
      icon: Users,
      title: 'For Donors',
      description: 'How to donate and track contributions',
      articles: 4,
      color: 'text-teal-500',
      content: [
        { title: 'How to make a donation', answer: 'Click "Donate" on any campaign page, enter your amount, and complete payment. You can donate as a guest or create an account to track your giving history.' },
        { title: 'Tracking your donations', answer: 'View all your donations in Dashboard > My Donations. You\'ll see campaign updates, how funds are being used, and impact reports.' },
        { title: 'Anonymous donations', answer: 'Check the "Donate anonymously" box during checkout to hide your name from the public donor list. The campaign creator will still receive your details.' },
        { title: 'Donation receipts', answer: 'Receipts are emailed automatically after each donation. Access all receipts in your account under Donation History.' },
      ],
    },
    {
      icon: TrendingUp,
      title: 'Campaign Success',
      description: 'Tips to reach your funding goal',
      articles: 7,
      color: 'text-orange-500',
      content: [
        { title: 'Promoting your campaign', answer: 'Share on social media, email friends and family, reach out to local news, and engage with online communities related to your cause.' },
        { title: 'Writing effective updates', answer: 'Post updates at least weekly. Share progress, thank donors by name, show how funds are being used, and include photos/videos.' },
        { title: 'Using AI tools', answer: 'Our AI assistant can help write compelling descriptions, suggest improvements, and generate social media posts. Access it from your campaign editor.' },
        { title: 'Engaging with donors', answer: 'Respond to comments promptly, send thank-you messages, and make donors feel part of your journey. Engaged donors often share your campaign.' },
        { title: 'Setting realistic goals', answer: 'Research similar campaigns and set achievable milestones. You can always increase your goal later as you gain momentum.' },
        { title: 'Adding media content', answer: 'Campaigns with videos raise 150% more on average. Include high-quality photos showing the impact of donations.' },
        { title: 'Timing your campaign', answer: 'Launches often perform better on Tuesdays and Wednesdays. Plan major sharing pushes around paydays and holidays.' },
      ],
    },
    {
      icon: FileText,
      title: 'Policies & Guidelines',
      description: 'Terms, privacy, and community guidelines',
      articles: 5,
      color: 'text-emerald-500',
      content: [
        { title: 'Terms of Service', answer: 'Our Terms of Service outline the rules for using FundTheHope. Key points: be honest, don\'t misuse funds, respect others, and follow local laws. FundTheHope acts as an intermediary platform and does not guarantee campaign outcomes.' },
        { title: 'Prohibited campaigns', answer: 'We don\'t allow campaigns for illegal activities, hate speech, weapons, drugs, gambling, or adult content. Review our full list in the Community Guidelines.' },
        { title: 'Privacy Policy', answer: 'We collect only necessary data to provide our services. Your information is encrypted and never sold. You can request data deletion at any time.' },
        { title: 'Intellectual property', answer: 'Only use images and content you own or have permission to use. Respect copyright and trademark laws in your campaign.' },
        { title: 'Account termination', answer: 'Accounts may be suspended for violating terms or fraudulent activity. Pending funds may be withheld or delayed pending investigation in cases of suspected violations.' },
      ],
    },
  ];

  const faqs = [
    {
      question: 'How do I create a campaign?',
      answer: 'Click "Start a Campaign" in the navigation, fill in your campaign details including title, description, goal amount, and images. After verification, your campaign will go live and you can start receiving donations.',
    },
    {
      question: 'What payment methods are accepted?',
      answer: 'We accept all major credit cards (Visa, Mastercard, American Express), debit cards, Apple Pay, and Google Pay. All payments are processed securely through Stripe.',
    },
    {
      question: 'How do I withdraw funds?',
      answer: 'Go to your Wallet page, add a bank account, and click "Withdraw". Funds typically arrive in 3-5 business days. Minimum withdrawal is $10.',
    },
    {
      question: 'What are the platform fees?',
      answer: 'FundTheHope charges 0% platform fees. Standard payment processing fees charged by payment providers (e.g., 2.9% + $0.30) apply.',
    },
    {
      question: 'How does verification work?',
      answer: 'Complete email verification, phone verification, and identity verification (KYC) to become fully verified. Verified campaigns receive a trust badge and have higher visibility.',
    },
    {
      question: 'Can I edit my campaign after publishing?',
      answer: 'Yes! You can update your campaign description, images, and goal at any time from your dashboard. Some changes may require re-verification.',
    },
    {
      question: 'What happens if I don\'t reach my goal?',
      answer: 'FundTheHope uses flexible funding - you keep all donations received even if you don\'t reach your goal. There\'s no penalty for not meeting your target.',
    },
    {
      question: 'What is the refund policy?',
      answer: 'Donations are generally non-refundable once processed. Refunds may be considered on a case-by-case basis solely at FundTheHope\'s discretion in cases of confirmed fraud or material policy violations. We do not guarantee refunds under any circumstances.',
    },
  ];

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <HelpCircle className="w-16 h-16 mx-auto mb-6 text-emerald-500" />
            <h1 className="text-4xl font-bold text-foreground mb-4">
              {t('helpCenterTitle')}
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              {t('helpCenterSubtitle')}
            </p>
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('searchHelpPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 text-lg bg-background"
              />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Visual Banner Section */}
          <div className="mb-16 rounded-3xl overflow-hidden bg-gradient-to-br from-emerald-500/10 to-teal-500/10 p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  We're Here to Help
                </h2>
                <p className="text-lg text-foreground/70 mb-6">
                  Our dedicated support team is available to assist you with any questions about fundraising, donations, or platform features. Browse our comprehensive guides or contact us directly.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a href="mailto:support@fundthehope.com">
                    <Button className="bg-gradient-to-r from-emerald-500 to-teal-600">
                      <Mail className="w-4 h-4 mr-2" />
                      Contact Support
                    </Button>
                  </a>
                  <Link to="/how-it-works">
                    <Button variant="outline">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Quick Start Guide
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.pexels.com/photos/8204396/pexels-photo-8204396.jpeg?auto=compress&cs=tinysrgb&w=400" 
                  alt="Customer support representative"
                  className="rounded-2xl object-cover h-48 w-full shadow-lg"
                />
                <img 
                  src="https://images.pexels.com/photos/8204312/pexels-photo-8204312.jpeg?auto=compress&cs=tinysrgb&w=400" 
                  alt="Support team helping user"
                  className="rounded-2xl object-cover h-48 w-full shadow-lg"
                />
              </div>
            </div>
          </div>

          {/* Categories Grid */}
          <h2 className="text-2xl font-bold text-foreground mb-6">Browse by Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <Card 
                  key={index} 
                  className="hover:shadow-lg transition-shadow cursor-pointer hover:border-emerald-500/50"
                  onClick={() => setSelectedCategory(category)}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl bg-secondary ${category.color}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">{category.title}</h3>
                        <p className="text-sm text-muted-foreground mb-2">{category.description}</p>
                        <span className="text-xs text-muted-foreground">{category.articles} articles</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Category Detail Modal */}
          {selectedCategory && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
                <div className="p-6 border-b border-border flex items-center justify-between bg-gradient-to-r from-emerald-500/10 to-teal-500/10">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-xl bg-secondary ${selectedCategory.color}`}>
                      <selectedCategory.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground">{selectedCategory.title}</h3>
                      <p className="text-sm text-muted-foreground">{selectedCategory.content.length} articles</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedCategory(null)} 
                    className="p-2 hover:bg-secondary rounded-full transition-colors"
                  >
                    <span className="text-2xl text-muted-foreground">&times;</span>
                  </button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                  <Accordion type="single" collapsible className="w-full">
                    {selectedCategory.content.map((article, idx) => (
                      <AccordionItem key={idx} value={`article-${idx}`}>
                        <AccordionTrigger className="text-left hover:text-emerald-500">
                          {article.title}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground">
                          {article.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
                <div className="p-4 border-t border-border bg-secondary/30">
                  <p className="text-sm text-muted-foreground text-center">
                    {t('stillNeedHelpDesc')} <a href="mailto:support@fundthehope.com" className="text-emerald-500 hover:underline">{t('contactSupport')}</a>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* FAQs */}
          <h2 className="text-2xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
          <Card className="mb-16">
            <CardContent className="pt-6">
              <Accordion type="single" collapsible className="w-full">
                {filteredFaqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              {filteredFaqs.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  No results found. Try a different search term.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Contact Section */}
          <h2 className="text-2xl font-bold text-foreground mb-6">{t('stillNeedHelp')}</h2>
          <div className="max-w-md mx-auto">
            <Card>
              <CardContent className="pt-6 text-center">
                <Mail className="w-10 h-10 mx-auto mb-4 text-emerald-500" />
                <h3 className="font-semibold text-foreground mb-2">Contact Support</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Get help via email within 24 hours
                </p>
                <Button 
                  variant="outline" 
                  className="w-full border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
                  onClick={() => setShowContactForm(true)}
                >
                  <Mail className="w-4 h-4 mr-2" /> Contact Support
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Contact Support Dialog */}
          <Dialog open={showContactForm} onOpenChange={(open) => {
            setShowContactForm(open);
            if (!open) {
              setContactSubmitted(false);
              setContactForm({ name: '', email: '', subject: '', message: '' });
              setContactError('');
            }
          }}>
            <DialogContent className="max-w-md bg-card border-border">
              <DialogHeader>
                <DialogTitle className="text-foreground flex items-center gap-2">
                  <Mail className="w-5 h-5 text-emerald-400" />
                  Contact Support
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Send us a message and we'll respond within 24 hours
                </DialogDescription>
              </DialogHeader>
              
              {contactSubmitted ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Message Sent!</h3>
                  <p className="text-muted-foreground text-sm">
                    We'll get back to you within 24 hours. Check your email for updates.
                  </p>
                  <Button
                    className="mt-4 bg-emerald-500 hover:bg-emerald-600"
                    onClick={() => setShowContactForm(false)}
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  {contactError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                      {contactError}
                    </div>
                  )}
                  <div>
                    <label className="text-sm text-foreground/80 mb-1 block">Your Name</label>
                    <Input
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      placeholder="John Doe"
                      className="bg-secondary/50 border-border text-foreground"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground/80 mb-1 block">Email Address</label>
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      placeholder="you@example.com"
                      className="bg-secondary/50 border-border text-foreground"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground/80 mb-1 block">Subject</label>
                    <Input
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                      placeholder="How can we help?"
                      className="bg-secondary/50 border-border text-foreground"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground/80 mb-1 block">Message</label>
                    <Textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      placeholder="Describe your issue in detail..."
                      className="bg-secondary/50 border-border text-foreground min-h-[100px]"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={contactLoading}
                    className="w-full bg-emerald-500 hover:bg-emerald-600"
                  >
                    {contactLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Send Message</>
                    )}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>

          {/* Quick Tips */}
          <div className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10">
            <h3 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
              Quick Tips for Campaign Success
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                'Complete your verification to earn trust badges',
                'Add high-quality images and videos to your campaign',
                'Share your campaign on social media regularly',
                'Post regular updates to keep donors engaged',
              ].map((tip, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-emerald-500">{index + 1}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{tip}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Support Statistics */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-emerald-500 mb-2">Responsive</div>
                <p className="text-sm text-muted-foreground">Support Team</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-emerald-500 mb-2">&lt;24h</div>
                <p className="text-sm text-muted-foreground">Average Response Time</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-emerald-500 mb-2">50+</div>
                <p className="text-sm text-muted-foreground">Help Articles</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-emerald-500 mb-2">98%</div>
                <p className="text-sm text-muted-foreground">Satisfaction Rate</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default HelpCenterPage;
